import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import { Add as AddIcon, Edit as EditIcon } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { IEBlockSize } from '../../../models/eBlockSize';
import { PageFilter } from '../../../state/app';
import { AppState } from '../../../state/configureStore';
import {
  clearEBlockSize,
  EBlockSizeListFilter,
  EBlockSizeState,
  fetchEBlockSizeList,
} from '../../../state/eBlockSize';
import {
  DateTimeDisplay,
  PageContainer,
  PageTitle,
  SearchField,
  SortLabel,
  TableLoading,
} from '../../components';
import { useFilter, useHasRole, useTableSorting } from '../../hooks';
import roleRoute from '../roleRoute.config';
import useStyles from './eBlockSize.style';

const EBlockSizeList: React.FC<RouteComponentProps> = (props) => {
  const { history } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const isDisabled = !useHasRole(roleRoute.createEBlockSize);
  const [filters, setFilters] = useFilter<EBlockSizeListFilter>({});
  const [paging, setPaging] = useState<PageFilter>({
    pageNumber: 0,
    pageSize: 15,
  });
  const [sort, setSort] = useTableSorting({
    orderBy: 'createdAt',
    direction: 'desc',
  });
  const {
    items: eBlockSizes,
    totalItems,
    loading,
  } = useSelector<AppState, EBlockSizeState>((state) => state.eBlockSize);

  const handleChangePageNumber = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPaging({ ...paging, pageNumber: newPage });
  };

  const handleChangePageSize = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaging({
      ...paging,
      pageSize: +event.target.value,
      pageNumber: 0,
    });
  };

  const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { target } = event;
    setFilters((prevState) => ({
      ...prevState,
      [target.name]: target.value,
    }));
    setPaging({
      ...paging,
      pageNumber: 0,
    });
  };

  const addNewEBlockSize = (): void => {
    history.push('/settings/eBlockSizes/form');
  };

  const editEBlockSize = (id: IEBlockSize['id']): void => {
    history.push(`/settings/eBlockSizes/form/${id}`);
  };

  useEffect(() => {
    dispatch(fetchEBlockSizeList(filters, { ...paging, sort }));
    return function cleanUp() {
      dispatch(clearEBlockSize());
    };
  }, [dispatch, paging, filters, sort]);

  return (
    <PageContainer>
      <PageTitle>EBlock Sizes</PageTitle>
      <div className={classes.filterContainer}>
        <SearchField
          filterKey="EBlockGroupProduct.CodeAndDesc.Contains"
          filters={filters}
          label="Group Product"
          onChange={handleChangeFilter}
        />
        <SearchField
          filterKey="Code.StartsWith"
          filters={filters}
          label="Code"
          onChange={handleChangeFilter}
        />
        <SearchField
          filterKey="Description.StartsWith"
          filters={filters}
          label="Description"
          onChange={handleChangeFilter}
        />
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          onClick={addNewEBlockSize}
          size="large"
        >
          <AddIcon className={classes.iconButton} />
          New EBlock Size
        </Button>
      </div>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>No.</TableCell>
            <TableCell>
              <SortLabel
                sort={sort}
                handleSortChange={setSort}
                field="eBlockGroupProduct.code"
              >
                Group Product
              </SortLabel>
            </TableCell>
            <TableCell>
              <SortLabel sort={sort} handleSortChange={setSort} field="code">
                Code
              </SortLabel>
            </TableCell>
            <TableCell>
              <SortLabel sort={sort} handleSortChange={setSort} field="description">
                Description
              </SortLabel>
            </TableCell>
            <TableCell>
              <SortLabel sort={sort} handleSortChange={setSort} field="createdAt">
                CreatedAt
              </SortLabel>
            </TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableLoading isLoading={loading} colSpan={4}>
            {eBlockSizes.map((eBlockSize, index) => (
              <TableRow key={eBlockSize.id}>
                <TableCell>{index + paging.pageSize * paging.pageNumber + 1}</TableCell>
                <TableCell>
                  {eBlockSize.eBlockGroupProduct
                    ? `${eBlockSize.eBlockGroupProduct!.code}: ${
                        eBlockSize.eBlockGroupProduct!.description
                      }`
                    : ''}
                </TableCell>
                <TableCell>{eBlockSize.code}</TableCell>
                <TableCell>{eBlockSize.description}</TableCell>
                <TableCell>
                  <DateTimeDisplay value={eBlockSize.createdAt} />
                </TableCell>
                <TableCell>
                  <IconButton
                    aria-label="edit"
                    size="small"
                    disabled={isDisabled}
                    onClick={() => editEBlockSize(eBlockSize.id)}
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableLoading>
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[15, 25, 35]}
        component="div"
        count={totalItems}
        rowsPerPage={paging.pageSize}
        page={paging.pageNumber}
        onPageChange={handleChangePageNumber}
        onRowsPerPageChange={handleChangePageSize}
      />
    </PageContainer>
  );
};

export default EBlockSizeList;
